import { useReportDateState } from "../hooks/useReportDateAccess";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FaDownload, FaPrint, FaSyncAlt } from "react-icons/fa";
import { API_BASE_URL } from "../config/constants";
import { formatStoredMoney } from "../utils/currency";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportControlBar from "../components/ReportControlBar";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { getOfflineFinanceDayBookReport } from "../offline/financeStore";

const ymd = (value) => {
  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const monthStart = () => ymd(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
const money = (value) => formatStoredMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const entryDate = (entry) => String(entry?.entry_date || entry?.date || "").slice(0, 10);

const DayBookEntryReportPage = () => {
  const [restaurantId, setRestaurantId] = useState(null);
  const [fromDate, setFromDate] = useReportDateState(monthStart);
  const [toDate, setToDate] = useReportDateState(() => ymd(new Date()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rows, setRows] = useState([]);
  const [paymentsByDate, setPaymentsByDate] = useState({});
  const reportRef = useRef(null);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const user = stored?.data || stored || {};
    const id = user?.id || user?.restaurant_id;
    if (!id) {
      toast.error("Restaurant information is missing. Please login again.");
      setLoading(false);
      return;
    }
    setRestaurantId(id);
  }, []);

  const post = useCallback(async (path, body) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.success === false) throw new Error(json?.message || "Unable to load day book");
    return json;
  }, []);

  const fetchReport = useCallback(async ({ refresh = false } = {}) => {
    if (!restaurantId) return;
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const common = { restaurantid: restaurantId, from_date: fromDate, to_date: toDate };
      let expenseJson;
      let incomeJson;
      let profitJson;
      let nextPayments;
      if (isFullOfflineModeEnabled()) {
        const localReport = await getOfflineFinanceDayBookReport(
          restaurantId,
          fromDate,
          toDate
        );
        expenseJson = { success: true, entries: localReport?.expense_entries || [] };
        incomeJson = { success: true, entries: localReport?.income_entries || [] };
        profitJson = localReport?.profit_report || {};
        nextPayments = localReport?.payments_by_date || {};
      } else {
        [expenseJson, incomeJson, profitJson] = await Promise.all([
          post("/finance/entries/list", { ...common, entry_type: "expense" }),
          post("/finance/entries/list", { ...common, entry_type: "income" }),
          post("/finance/profit-report", common),
        ]);
      }
      const trend = Array.isArray(profitJson?.daily_trend) ? profitJson.daily_trend : [];
      if (!nextPayments) {
        const salesDates = [...new Set(trend
          .filter((day) => Number(day.sales || 0) !== 0)
          .map((day) => String(day.date || "").slice(0, 10))
          .filter(Boolean))];
        const paymentResults = await Promise.all(salesDates.map(async (date) => {
          try {
            const json = await post("/report-v2/payment-methods", { restaurantid: restaurantId, date });
            const incomingModes = (Array.isArray(json?.modes) ? json.modes : []).map((mode) => ({
              key: String(mode.mode || "other").trim().toLowerCase(),
              label: String(mode.mode || "Other").trim(),
              amount: Number(mode.total_amount || 0),
            }));
            const modeMap = Object.fromEntries(incomingModes.map((mode) => [mode.key, mode]));
            const modes = [
              ...[["cash", "Cash"], ["upi", "UPI"], ["card", "Card"], ["credit", "Credit"]]
                .map(([key, label]) => modeMap[key] || { key, label, amount: 0 }),
              ...incomingModes.filter((mode) => !["cash", "upi", "card", "credit"].includes(mode.key)),
            ];
            return [date, modes];
          } catch (_) {
            return [date, []];
          }
        }));
        nextPayments = Object.fromEntries(paymentResults);
      }
      setPaymentsByDate(nextPayments);
      const inRange = (date) => date && date >= fromDate && date <= toDate;
      const manualRows = [
        ...(Array.isArray(expenseJson?.entries) ? expenseJson.entries : []).map((entry) => ({
          id: `expense-${entry.id}`,
          date: entryDate(entry),
          particulars: entry.category_name || "Expense",
          notes: entry.notes || "",
          type: "Expense",
          income: 0,
          expense: Number(entry.amount || 0),
        })),
        ...(Array.isArray(incomeJson?.entries) ? incomeJson.entries : []).map((entry) => ({
          id: `income-${entry.id}`,
          date: entryDate(entry),
          particulars: entry.category_name || "Other Income",
          notes: entry.notes || "",
          type: "Other Income",
          income: Number(entry.amount || 0),
          expense: 0,
        })),
      ].filter((entry) => inRange(entry.date));
      const salesRows = trend
        .filter((day) => inRange(String(day.date || "").slice(0, 10)) && Number(day.sales || 0) !== 0)
        .map((day) => ({
          id: `sales-${day.date}`,
          date: String(day.date).slice(0, 10),
          particulars: "Daily Sales",
          notes: `${Number(day.orders_count || 0)} order${Number(day.orders_count || 0) === 1 ? "" : "s"}`,
          type: "Sales",
          income: Number(day.sales || 0),
          expense: 0,
        }));
      setRows([...manualRows, ...salesRows].sort((a, b) => a.date.localeCompare(b.date) || a.type.localeCompare(b.type)));
    } catch (error) {
      toast.error(error?.message || "Unable to load Day Book Entry Report");
      setRows([]);
      setPaymentsByDate({});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [restaurantId, fromDate, toDate, post]);

  useEffect(() => { if (restaurantId) fetchReport(); }, [restaurantId, fetchReport]);

  const calculatedRows = useMemo(() => {
    let balance = 0;
    return rows.map((row) => {
      balance += Number(row.income || 0) - Number(row.expense || 0);
      return { ...row, balance };
    });
  }, [rows]);
  const totals = useMemo(() => calculatedRows.reduce((result, row) => ({
    income: result.income + row.income,
    expense: result.expense + row.expense,
  }), { income: 0, expense: 0 }), [calculatedRows]);
  const grouped = useMemo(() => calculatedRows.reduce((result, row) => {
    (result[row.date] ||= []).push(row);
    return result;
  }, {}), [calculatedRows]);
  const paymentTotals = useMemo(() => Object.values(paymentsByDate).flat().reduce((totals, mode) => {
    const key = mode.key || "other";
    if (!totals[key]) totals[key] = { label: mode.label || "Other", amount: 0 };
    totals[key].amount += Number(mode.amount || 0);
    return totals;
  }, {}), [paymentsByDate]);
  const preferredPaymentModes = useMemo(() => {
    const order = ["cash", "upi", "card", "credit"];
    return Object.entries(paymentTotals).sort(([a], [b]) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
  }, [paymentTotals]);
  const paymentText = (date) => (paymentsByDate[date] || [])
    .filter((mode) => mode.amount !== 0)
    .map((mode) => `${mode.label}: ${mode.amount}`)
    .join(" | ");
  const exportRows = calculatedRows.map((row) => [row.date, row.particulars, row.notes, row.type, row.type === "Sales" ? paymentText(row.date) : "", row.income, row.expense, row.balance]);

  return <Page>
    <ReportPageHeader
      title="Day Book Entry Report"
      subtitle="Day-wise sales, payment collections, other income and expenses."
      stats={[
        { label: "Total Income", value: money(totals.income) },
        { label: "Total Expense", value: money(totals.expense) },
        { label: "Closing Balance", value: money(totals.income - totals.expense) },
      ]}
    />
    <ReportControlBar>
      <ReportPeriodPicker fromDate={fromDate} toDate={toDate} onChange={({ from, to }) => { setFromDate(from); setToDate(to); }} />
      <PrimaryBtn type="button" onClick={() => fetchReport({ refresh: true })} disabled={refreshing}>
        <FaSyncAlt /> {refreshing ? "Refreshing…" : "Refresh"}
      </PrimaryBtn>
    </ReportControlBar>
    <PaymentSummary>{preferredPaymentModes.map(([key, mode]) => <div key={key}><span>{mode.label} Received</span><strong>{money(mode.amount)}</strong></div>)}{!preferredPaymentModes.length && !loading ? <small>No payment-mode sales found for this range.</small> : null}</PaymentSummary>
    {loading ? <Empty>Loading day book…</Empty> : <Report ref={reportRef}>
      {Object.entries(grouped).map(([date, dayRows]) => <Day key={date}><DayHead><b>{date}</b><span>{dayRows.length} entries</span></DayHead>{(paymentsByDate[date] || []).length > 0 && <DayPayments>{paymentsByDate[date].map((mode) => <div key={mode.key}><span>{mode.label}</span><b>{money(mode.amount)}</b></div>)}</DayPayments>}<TableWrap><Table><thead><tr><th>Particulars</th><th>Notes</th><th>Type</th><th>Income</th><th>Expense</th><th>Balance</th></tr></thead><tbody>{dayRows.map((row) => <tr key={row.id}><td>{row.particulars}</td><td>{row.notes || "—"}</td><td><Badge $type={row.type}>{row.type}</Badge></td><td className="income">{row.income ? money(row.income) : "—"}</td><td className="expense">{row.expense ? money(row.expense) : "—"}</td><td>{money(row.balance)}</td></tr>)}</tbody></Table></TableWrap></Day>)}
      {!calculatedRows.length && <Empty>No entries found for this date range.</Empty>}
    </Report>}
    <Actions><button onClick={() => downloadPDF(reportRef.current, `day_book_${fromDate}_to_${toDate}.pdf`)}><FaDownload /> PDF</button><button onClick={() => printThermal(reportRef.current, `day_book_${fromDate}_to_${toDate}`)}><FaPrint /> Thermal Print</button><button onClick={() => printThermalViaUsb(reportRef.current, `day_book_${fromDate}_to_${toDate}`)}><FaPrint /> Thermal Print via USB</button><button onClick={() => downloadExcel(`day_book_${fromDate}_to_${toDate}.xlsx`, ["Date", "Particulars", "Notes", "Type", "Payment Breakdown", "Income", "Expense", "Balance"], exportRows)}>Excel</button><button onClick={() => downloadCSV(`day_book_${fromDate}_to_${toDate}.csv`, ["Date", "Particulars", "Notes", "Type", "Payment Breakdown", "Income", "Expense", "Balance"], exportRows)}>CSV</button></Actions>
  </Page>;
};

export default DayBookEntryReportPage;

const Page = styled.div`min-height:100vh;padding:18px;background:#f3f6fa;color:#0f172a;@media(max-width:650px){padding:12px}`;
const PrimaryBtn = styled.button`background:linear-gradient(135deg,var(--app-primary),var(--app-primary-hover));color:#fff;border:0;border-radius:10px;padding:9px 14px;font-weight:800;display:inline-flex;align-items:center;gap:7px;cursor:pointer;box-shadow:0 4px 12px rgba(7,95,158,.15);&:disabled{opacity:.65;cursor:not-allowed}`;
const PaymentSummary = styled.div`display:flex;gap:10px;overflow-x:auto;margin-bottom:14px;div{min-width:145px;background:var(--app-primary-soft);border:1px solid var(--app-primary-soft);border-radius:13px;padding:12px;display:grid;gap:4px}span{font-size:11px;color:#1e40af;font-weight:800}strong{color:#172554;font-size:1.05rem}small{padding:12px;color:#64748b}`;
const Report = styled.div`display:grid;gap:13px`;
const Day = styled.section`background:#fff;border:1px solid #e2e8f0;border-radius:15px;overflow:hidden`;
const DayHead = styled.div`padding:12px 15px;background:var(--app-primary-soft);display:flex;justify-content:space-between;color:#1e3a8a;span{font-size:12px}`;
const DayPayments = styled.div`display:flex;gap:8px;flex-wrap:wrap;padding:10px 13px;background:#f8fafc;border-bottom:1px solid #e2e8f0;div{display:flex;gap:7px;align-items:center;padding:6px 9px;background:#fff;border:1px solid var(--app-primary-soft);border-radius:9px;font-size:12px}span{color:#64748b;font-weight:700}b{color:#0f172a}`;
const TableWrap = styled.div`overflow-x:auto`;
const Table = styled.table`width:100%;border-collapse:collapse;min-width:720px;th,td{padding:10px 12px;text-align:left;border-bottom:1px solid #eef2f7;font-size:13px}th{font-size:11px;text-transform:uppercase;color:#64748b;background:#f8fafc}.income{color:#047857;font-weight:800}.expense{color:#dc2626;font-weight:800}`;
const Badge = styled.span`display:inline-block;padding:4px 8px;border-radius:999px;font-size:11px;font-weight:800;background:${({$type})=>$type === "Expense" ? "#fef2f2" : "#ecfdf5"};color:${({$type})=>$type === "Expense" ? "#b91c1c" : "#047857"}`;
const Empty = styled.div`padding:40px;text-align:center;background:#fff;border:1px solid #e2e8f0;border-radius:15px;color:#64748b`;
const Actions = styled.div`display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap;margin-top:15px;button{border:0;border-radius:10px;padding:10px 13px;background:var(--app-primary);color:#fff;font-weight:800;display:flex;align-items:center;gap:6px;cursor:pointer}`;
